import citiesAndDelegations from "../data/citiesAndDelegations";

// "Je recherche un bien": same vocabularies and limits as the API (PropertyRequestRules).
export const TRANSACTIONS = ["sale", "rent", "vacation"];
export const CATEGORIES = ["Appartement", "Maison", "Commercial", "Terrain", "Autres"];
const VACATION_CATEGORIES = ["Appartement", "Maison", "Commercial"];
const ATTRIBUTES = {
  sale: ["parking", "furnished"],
  rent: ["parking", "furnished"],
  vacation: ["kitchen", "ac", "pool", "garden", "barbecue", "beachAccess", "workspace", "washer", "wifi", "tv"],
};
// Existing translations of the publication categories.
export const CATEGORY_KEYS = { Appartement: "apartment", Maison: "house", Commercial: "commercial", Terrain: "land", Autres: "others" };
export const CONTACT_PREFERENCES = ["email", "phone", "both"];
export const MAX_DELEGATIONS = 5;
export const MAX_TEXT = 1000;
const MAX_AMOUNT = 1000000000;
const MAX_BEDROOMS = 30;
const EMAIL = /^[^@\s<>()[\],;:"]+@[^@\s<>()[\],;:"]+\.[^@\s<>()[\],;:"]{2,}$/;

export const emptyCriteria = () => ({
  transaction: "", categories: [], city: "", delegations: [],
  budgetMin: "", budgetMax: "", surfaceMin: "", surfaceMax: "", bedroomsMin: "", attributes: [],
});

export const categoriesFor = (transaction) => (transaction === "vacation" ? VACATION_CATEGORIES : CATEGORIES);
export const attributesFor = (transaction) => ATTRIBUTES[transaction] || [];
export const cities = () => Object.keys(citiesAndDelegations);
export const delegationsOf = (city) => citiesAndDelegations[city] || [];
export const isLandOnly = (criteria) => criteria.categories.length > 0 && criteria.categories.every((c) => c === "Terrain");
// Bedrooms and equipment only make sense once a non-land type is chosen.
export const allowsResidential = (criteria) => criteria.categories.length > 0 && !isLandOnly(criteria);

// Keeps the criteria coherent after a change (transaction, type or city).
export function coherentCriteria(criteria) {
  const next = { ...criteria };
  next.categories = criteria.categories.filter((c) => categoriesFor(next.transaction).includes(c));
  next.attributes = criteria.attributes.filter((a) => attributesFor(next.transaction).includes(a));
  next.delegations = criteria.delegations.filter((d) => delegationsOf(next.city).includes(d)).slice(0, MAX_DELEGATIONS);
  if (!allowsResidential(next)) { next.bedroomsMin = ""; next.attributes = []; }
  return next;
}

// "250 000" or "1200,5" are accepted; "" means not set; NaN means invalid.
export function parseNumber(value) {
  const text = String(value ?? "").replace(/[\s  ]/g, "").replace(",", ".");
  if (text === "") return null;
  return /^\d+(\.\d+)?$/.test(text) ? Number(text) : NaN;
}

function range(errors, minKey, maxKey, values, max) {
  const [min, high] = [parseNumber(values[minKey]), parseNumber(values[maxKey])];
  if (Number.isNaN(min) || min > max) errors[minKey] = "invalidNumber";
  if (Number.isNaN(high) || high > max) errors[maxKey] = "invalidNumber";
  if (!errors[minKey] && !errors[maxKey] && min != null && high != null && min > high) errors[maxKey] = "minAboveMax";
}

// Returns { field: errorKey } with keys under propertyRequest.errors.
export function validateCriteria(criteria, text = "") {
  const errors = {};
  if (!TRANSACTIONS.includes(criteria.transaction)) errors.transaction = "transactionRequired";
  if (criteria.categories.length === 0) errors.categories = "categoryRequired";
  else if (criteria.categories.some((c) => !categoriesFor(criteria.transaction).includes(c))) errors.categories = "categoryInvalid";
  if (!criteria.city) errors.city = "cityRequired";
  else if (!citiesAndDelegations[criteria.city]) errors.city = "cityInvalid";
  if (criteria.delegations.length > MAX_DELEGATIONS) errors.delegations = "tooManyDelegations";
  else if (criteria.delegations.some((d) => !delegationsOf(criteria.city).includes(d))) errors.delegations = "delegationInvalid";
  range(errors, "budgetMin", "budgetMax", criteria, MAX_AMOUNT);
  range(errors, "surfaceMin", "surfaceMax", criteria, MAX_AMOUNT);
  const bedrooms = parseNumber(criteria.bedroomsMin);
  if (Number.isNaN(bedrooms) || (bedrooms != null && (!Number.isInteger(bedrooms) || bedrooms > MAX_BEDROOMS))) errors.bedroomsMin = "invalidBedrooms";
  if (String(text || "").length > MAX_TEXT) errors.text = "textTooLong";
  return errors;
}

// Separators are ignored; Arabic-Indic digits are accepted, as on the server.
export function normalizePhone(value) {
  let result = "";
  for (const char of String(value || "").trim()) {
    const arabic = "٠١٢٣٤٥٦٧٨٩".indexOf(char);
    if (/\d/.test(char)) result += char;
    else if (arabic >= 0) result += String(arabic);
    else if (char === "+" && result === "") result = "+";
    else if (!" .-()".includes(char)) return null;
  }
  return /^\+?\d{8,15}$/.test(result) ? result : null;
}

export function validateContact(contact) {
  const errors = {};
  if (!CONTACT_PREFERENCES.includes(contact.preference)) errors.preference = "preferenceRequired";
  if (contact.preference === "email" || contact.preference === "both") {
    const email = String(contact.email || "").trim();
    if (!email) errors.email = "emailRequired";
    else if (email.length > 200 || !EMAIL.test(email)) errors.email = "emailInvalid";
  }
  if (contact.preference === "phone" || contact.preference === "both") {
    if (!String(contact.phone || "").trim()) errors.phone = "phoneRequired";
    else if (!normalizePhone(contact.phone)) errors.phone = "phoneInvalid";
  }
  return errors;
}

// API body: the owner always comes from the Firebase token, never from the client.
export function toPayload(criteria, text, contact) {
  const number = (value) => parseNumber(value);
  const wantsEmail = contact.preference !== "phone";
  const wantsPhone = contact.preference !== "email";
  return {
    transaction: criteria.transaction,
    categories: [...criteria.categories],
    city: criteria.city,
    delegations: [...criteria.delegations],
    budgetMin: number(criteria.budgetMin), budgetMax: number(criteria.budgetMax),
    surfaceMin: number(criteria.surfaceMin), surfaceMax: number(criteria.surfaceMax),
    bedroomsMin: allowsResidential(criteria) ? number(criteria.bedroomsMin) : null,
    attributes: allowsResidential(criteria) ? [...criteria.attributes] : [],
    freeText: String(text || "").trim() || null,
    contactPreference: contact.preference,
    contactEmail: wantsEmail ? String(contact.email || "").trim() : null,
    contactPhone: wantsPhone ? normalizePhone(contact.phone) : null,
  };
}

// Criteria only (analysis and matching): never contact details.
export function toCriteria(criteria) {
  const { contactPreference, contactEmail, contactPhone, freeText, ...rest } = toPayload(criteria, "", { preference: "email" });
  return rest;
}

export const CRITERIA_FIELDS = ["transaction", "categories", "city", "delegations", "budgetMin", "budgetMax", "surfaceMin", "surfaceMax", "bedroomsMin", "attributes"];
const isSet = (value) => (Array.isArray(value) ? value.length > 0 : value !== "" && value != null);

// Manual criteria sent to the analysis: fields entered by the user (a field filled by the analysis, "ai" or "auto", may be replaced).
export function manualCriteria(criteria, sources = {}) {
  const manual = {};
  for (const field of CRITERIA_FIELDS)
    if (isSet(criteria[field]) && sources[field] !== "ai" && sources[field] !== "auto") manual[field] = criteria[field];
  return Object.keys(manual).length ? toCriteria({ ...emptyCriteria(), ...manual }) : null;
}

// After an edit, changed fields become "manual"; cleared fields lose their source.
export function updateSources(before, after, sources = {}) {
  const next = { ...sources };
  for (const field of CRITERIA_FIELDS) {
    if (!isSet(after[field])) delete next[field];
    else if (JSON.stringify(before[field]) !== JSON.stringify(after[field])) next[field] = "manual";
  }
  return next;
}

// Merged criteria of the analysis -> form state (strings for numbers), kept coherent.
export function fromCriteria(criteria = {}) {
  const text = (value) => (value == null ? "" : String(value));
  return coherentCriteria({
    transaction: criteria.transaction || "", categories: criteria.categories || [], city: criteria.city || "",
    delegations: criteria.delegations || [], budgetMin: text(criteria.budgetMin), budgetMax: text(criteria.budgetMax),
    surfaceMin: text(criteria.surfaceMin), surfaceMax: text(criteria.surfaceMax), bedroomsMin: text(criteria.bedroomsMin),
    attributes: criteria.attributes || [],
  });
}

// Owner view returned by the API -> editable form state.
export function fromRequest(request) {
  const text = (value) => (value == null ? "" : String(value));
  return {
    criteria: coherentCriteria({
      transaction: request.transaction || "", categories: request.categories || [], city: request.city || "",
      delegations: request.delegations || [], budgetMin: text(request.budgetMin), budgetMax: text(request.budgetMax),
      surfaceMin: text(request.surfaceMin), surfaceMax: text(request.surfaceMax),
      bedroomsMin: text(request.bedroomsMin), attributes: request.attributes || [],
    }),
    text: request.freeText || "",
    contact: { preference: request.contactPreference || "email", email: request.contactEmail || "", phone: request.contactPhone || "" },
  };
}
